import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Button, Card, Col, Form, Input, InputNumber, Row, App, Select, 
  Checkbox, Upload, Space, Typography, Tag, Divider, Modal, Switch, Table, Tooltip, Collapse, Alert 
} from 'antd';
import { 
  PictureOutlined, ArrowLeftOutlined, PlusOutlined, DeleteOutlined, 
  UpOutlined, DownOutlined, ExclamationCircleOutlined, SearchOutlined 
} from '@ant-design/icons';
import { 
  getRoutineById, 
  createRoutine, 
  updateRoutine, 
  uploadRoutineMedia, 
  checkRoutineTitleExists,
  publishRoutine
} from '../../services/routines';
import { listCatalog } from '../../services/catalogs';
import { resolveMedia } from '../../utils/media';
import { getPreparationCatalog } from '../../services/preparations';
import { listExercisesVariants } from '../../services/exercises';

const { useApp } = App;
const { Dragger } = Upload;
const { Text, Title } = Typography;

const DAYS_OF_WEEK = ['Día 1', 'Día 2', 'Día 3', 'Día 4', 'Día 5', 'Día 6', 'Día 7'];

// Valores de programación: permiten un número o un rango controlado (valor - valor).
const RANGE_VALUE_REGEX = /^\d+(?:\.\d+)?(?:\s*-\s*\d+(?:\.\d+)?)?$/;

const normalizeRangeValue = (value) => {
  if (value === null || value === undefined || value === '') return value;

  const normalized = String(value).replace(/\s*-\s*/g, ' - ').trim();
  return RANGE_VALUE_REGEX.test(normalized) ? normalized : value;
};

const validateRangeValue = (value, allowZero = false) => {
  if (value === null || value === undefined || value === '') return true;

  const normalized = String(value).trim();
  if (!RANGE_VALUE_REGEX.test(normalized)) return false;

  const values = normalized.split('-').map((part) => Number(part.trim()));
  const minValue = allowZero ? 0 : 1;

  if (values.some((number) => !Number.isFinite(number) || number < minValue)) return false;
  if (values.length === 2 && values[0] > values[1]) return false;

  return true;
};

const RangeValueInput = ({ value, onChange, placeholder, allowZero = false }) => (
  <Input
    value={value ?? ''}
    placeholder={placeholder}
    inputMode="decimal"
    onChange={(event) => {
      const rawValue = event.target.value;
      const sanitizedValue = rawValue.replace(/[^0-9.\-\s]/g, '');
      onChange(normalizeRangeValue(sanitizedValue));
    }}
    onBlur={(event) => {
      const normalizedValue = normalizeRangeValue(event.target.value);
      if (normalizedValue !== event.target.value) {
        onChange(normalizedValue);
      }
    }}
    status={value && !validateRangeValue(value, allowZero) ? 'error' : undefined}
  />
);

const parseRangeValue = (value) => {
  if (value === null || value === undefined || value === '') return null;

  const normalized = String(value).trim();
  if (!RANGE_VALUE_REGEX.test(normalized)) return null;

  const values = normalized.split('-').map((part) => Number(part.trim()));
  if (values.length === 1) return { min: values[0], max: values[0], isRange: false };

  return { min: values[0], max: values[1], isRange: true };
};

const getSeriesCountFromValue = (value) => {
  const parsed = parseRangeValue(value);
  return parsed ? parsed.min : null;
};

const validateSeriesValue = (value) => {
  const parsed = parseRangeValue(value);
  if (!parsed) return false;
  return Number.isInteger(parsed.min) && Number.isInteger(parsed.max) && parsed.min >= 1 && parsed.max >= 1 && parsed.min <= parsed.max;
};

const createEmptyDays = () => {
  return DAYS_OF_WEEK.map((dayName, index) => ({
    id: `day-${Date.now()}-${index}`,
    title: dayName,
    day_number: index + 1,
    is_rest: true,
    session_name: '',
    has_warmup: false,
    warm_up_id: null,
    has_cooldown: false,
    stretch_id: null,
    blocks: []
  }));
};

const RoutineUpsertPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  
  const navigate = useNavigate();
  const { message, modal } = useApp();
  const [form] = Form.useForm();

  const isSpecialFocusWatched = Form.useWatch('is_special_focus', form);
  const focusIdWatched = Form.useWatch('routine_focus_id', form);
  const durationValueWatched = Form.useWatch('durationValue', form);

  const [loading, setLoading] = useState(false);
  const [routineStatus, setRoutineStatus] = useState('BORRADOR');
  
  // Archivo e imágenes
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(undefined);
  const [imageRemoved, setImageRemoved] = useState(false);
  const [originalPreviewPath, setOriginalPreviewPath] = useState(null);

  // Catálogos
  const [levels, setLevels] = useState([]);
  const [focuses, setFocuses] = useState([]);
  const [durations, setDurations] = useState([]);
  const [locations, setLocations] = useState([]);
  const [plans, setPlans] = useState([]);
  const [paradigms, setParadigms] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [executors, setExecutors] = useState([]);
  
  // Catálogos para programación
  const [exerciseCatalog, setExerciseCatalog] = useState([]);
  const [miniRoutinesCatalog, setMiniRoutinesCatalog] = useState([]);

  // Estado de Semanas (Estructura dinámica de programación)
  const [weeks, setWeeks] = useState([
    {
      id: 1,
      title: 'Semana 1',
      week_number: 1,
      days: createEmptyDays()
    }
  ]);

  // Modales de Selección de Ejercicios
  const [exerciseModalVisible, setExerciseModalVisible] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState(null); // { weekId, dayId, blockId }
  const [exerciseSearch, setExerciseSearch] = useState('');

  // Estado de expansión de la programación.
  // Todo inicia colapsado para evitar que una semana extensa ocupe toda la pantalla.
  const [expandedWeeks, setExpandedWeeks] = useState([]);
  const [expandedDays, setExpandedDays] = useState([]);
  const [expandedBlocks, setExpandedBlocks] = useState([]);

  // Copia de semanas
  const [copyWeekModalVisible, setCopyWeekModalVisible] = useState(false);
  const [copySourceWeekId, setCopySourceWeekId] = useState(null);
  const [copyTargetWeekIds, setCopyTargetWeekIds] = useState([]);

  // Copia de días
  const [copyDayModalVisible, setCopyDayModalVisible] = useState(false);
  const [copySourceDay, setCopySourceDay] = useState(null);
  const [copyTargetWeekId, setCopyTargetWeekId] = useState(null);
  const [copyTargetDayId, setCopyTargetDayId] = useState(null);

  // Control de cambios sin guardar. Las acciones de programación son locales
  // y únicamente se persisten al presionar Guardar/Guardar y publicar.
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const isHydratingRef = useRef(true);
  const saveCompletedRef = useRef(false);

  // Control de errores al publicar
  const [publishErrors, setPublishErrors] = useState([]);

  // En creación, el estado inicial representa un formulario limpio.
  useEffect(() => {
    if (!isEdit) {
      const timer = setTimeout(() => {
        isHydratingRef.current = false;
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isEdit]);

  // Cambios de la estructura local marcan el formulario como modificado.
  useEffect(() => {
    if (!isHydratingRef.current) {
      setHasUnsavedChanges(true);
    }
  }, [weeks, imageFile, imageRemoved]);

  // Evita perder cambios al recargar/cerrar la pestaña.
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!hasUnsavedChanges || loading || saveCompletedRef.current) return;
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, loading]);

  const confirmLeave = useCallback((onConfirm) => {
    if (!hasUnsavedChanges || loading) {
      onConfirm();
      return;
    }

    modal.confirm({
      title: '¿Salir sin guardar?',
      icon: <ExclamationCircleOutlined />,
      content: 'Hay cambios sin guardar. Si sales ahora, se perderá toda la configuración realizada desde el último guardado.',
      okText: 'Salir sin guardar',
      okType: 'danger',
      cancelText: 'Continuar editando',
      onOk: onConfirm,
    });
  }, [hasUnsavedChanges, loading, modal]);

  // Carga unificada de catálogos
  useEffect(() => {
    const loadCatalogsData = async () => {
      const catalogSetters = {
        routine_levels: setLevels,
        routine_focuses: setFocuses,
        routine_durations: setDurations,
        routine_locations: setLocations,
        routine_plans: setPlans,
        training_paradigms: setParadigms,
        equipment: setEquipment,
        executors: setExecutors,
        // exercises: setExerciseCatalog,
        // preparations: setMiniRoutinesCatalog,
      };

      for (const cat of Object.keys(catalogSetters)) {
        try {
          const catalog = await listCatalog(cat);
          catalogSetters[cat](catalog || []);
        } catch (error) {
          console.error(`Error al cargar catálogo ${cat}:`, error);
        }
      }
    };

    const loadMiniRoutines = async (type) => {
        try {
          const miniRoutines = await getPreparationCatalog(type);
          setMiniRoutinesCatalog(miniRoutines || []);
        } catch (error) {
          console.error('Error al cargar mini rutinas:', error);
      }
    };
    const loadExercises = async () => {
        try {
          const items = await listExercisesVariants({
            search: undefined,
            page: 1,
            pageSize: 500,
          });
    
          let filteredItems = items || [];
    
          setExerciseCatalog(filteredItems);
        } catch (error) {
          console.error('Error al cargar ejercicios:', error);
      }
    };
    

    loadCatalogsData();
    loadMiniRoutines('ALL');
    loadExercises();
  }, []);

  // Manejo del cambio de duración estimada con advertencia de pérdida de semanas
  const handleDurationChange = (newVal) => {
    if (!newVal || newVal < 1) return;
    
    if (weeks.length > newVal) {
      modal.confirm({
        title: '¿Reducir la duración del plan?',
        icon: <ExclamationCircleOutlined />,
        content: `Ha configurado ${weeks.length} semanas. Reducir la duración a ${newVal} semana(s) eliminará las semanas sobrantes y toda su programación.`,
        okText: 'Sí, reducir',
        okType: 'danger',
        cancelText: 'Cancelar',
        onOk() {
          setWeeks((prev) => prev.slice(0, newVal));
          form.setFieldValue('durationValue', newVal);
        },
        onCancel() {
          form.setFieldValue('durationValue', weeks.length);
        }
      });
    } else {
      form.setFieldValue('durationValue', newVal);
    }
  };

  // Options memorizadas
  const normalFocusOptions = useMemo(() => {
    return focuses
      .filter((f) => f.is_wizard_option && f.goal_group === 'NORMAL')
      .map((f) => ({ value: f.id, label: f.wizard_label || f.name || f.code }));
  }, [focuses]);

  const specialFocusOptions = useMemo(() => {
    return focuses
      .filter((f) => f.is_wizard_option && f.goal_group === 'SPECIAL')
      .map((f) => ({ value: f.id, label: f.wizard_label || f.name || f.code }));
  }, [focuses]);

  const paradigmEnabled = useMemo(() => {
    const selected = focuses.find((f) => f.id === focusIdWatched);
    return selected?.code === 'hypertrophy';
  }, [focuses, focusIdWatched]);

  const paradigmOptions = useMemo(() => {
    return paradigms.map((p) => ({ value: p.id, label: p.name || p.code }));
  }, [paradigms]);

  const contextOptions = useMemo(() => {
    const options = [];
    locations.forEach((loc) => {
      if (loc.requires_equipment) {
        equipment.forEach((eq) => {
          options.push({
            label: `${loc.name} — ${eq.name}`,
            value: JSON.stringify({ routine_location_id: loc.id, equipment_id: eq.id }),
          });
        });
      } else {
        options.push({
          label: loc.name,
          value: JSON.stringify({ routine_location_id: loc.id, equipment_id: null }),
        });
      }
    });
    return options;
  }, [locations, equipment]);

  // Cargar entidad si es edición
  useEffect(() => {
    if (!isEdit || !id) return;

    isHydratingRef.current = true;
    setLoading(true);
    getRoutineById(id)
      .then((routine) => {
        setRoutineStatus(routine.status?.name || 'BORRADOR');

        const selectedContextValues = (routine.contexts || []).map((ctx) =>
          JSON.stringify({
            routine_location_id: ctx.routine_location_id,
            equipment_id: ctx.equipment_id || null,
          })
        );

        const isSpecial = Boolean(routine.is_special_focus);

        form.setFieldsValue({
          title: routine.title,
          short_description: routine.short_description || undefined,
          routine_level_id: routine.level?.id,
          routine_focus_id: routine.focus?.id,
          training_paradigm_id: routine.training_paradigm?.id,
          special_focus_ids: routine.special_focus_ids || (routine.special_focuses ? routine.special_focuses.map(f => f.id) : []),
          is_special_focus: isSpecial,
          routine_plan_id: routine.plan?.id,
          contexts: selectedContextValues,
          frequency_ids: routine.frequencies?.map((f) => f.id) || [],
          executor_ids: routine.executors?.map((e) => e.id) || [],
          durationValue: routine.duration_config?.value ?? 1,
          durationUnit: routine.duration_config?.unit ?? 'weeks',
        });

        if (routine.weekly_structure && Array.isArray(routine.weekly_structure) && routine.weekly_structure.length > 0) {
          setWeeks(routine.weekly_structure.map((week) => ({
            ...week,
            days: (week.days || []).map((day) => ({
              ...day,
              blocks: (day.blocks || []).map((block, blockIndex) => ({
                ...block,
                block_number: Number(block.block_number ?? blockIndex + 1),
                exercises: (block.exercises || []).map((exercise, exerciseIndex) => {
                  const baseName = exercise.exercise?.title ?? `Ejercicio ${exerciseIndex + 1}`;
                  const equipmentName = exercise.equipment?.name;
                  const formattedName = equipmentName ? `${baseName} - ${equipmentName}` : baseName;
                  return {
                    ...exercise,
                    exercise_name: formattedName,
                    displayLabel: formattedName,
                    exercise_order: Number(exercise.exercise_order ?? exerciseIndex + 1),
                    sets_config: (exercise.sets_config || []).map((set, setIndex) => ({
                      ...set,
                      set_number: Number(set.set_number ?? setIndex + 1)
                    }))
                  };
                })
              }))
            }))
          })));
        }

        if (routine.preview_path) {
          setOriginalPreviewPath(routine.preview_path);
          setImagePreview(resolveMedia(routine.preview_path));
        }

        isHydratingRef.current = false;
        setHasUnsavedChanges(false);
      })
      .catch((e) => {
        isHydratingRef.current = false;
        console.error('Error al cargar la rutina:', e);
        message.error('No se pudo cargar la información de la rutina');
      })  
      .finally(() => {
        setLoading(false);
      });
  }, [id, isEdit, form, message]);

  const validateTitle = useCallback(async (_, value) => {
    if (!value || !value.trim()) return Promise.resolve();
    const exists = await checkRoutineTitleExists(value.trim(), isEdit ? Number(id) : null);
    if (exists) {
      return Promise.reject(new Error('Este título ya está registrado.'));
    }
    return Promise.resolve();
  }, [isEdit, id]);

  const uploadProps = useMemo(
    () => ({
      name: 'file',
      multiple: false,
      maxCount: 1,
      beforeUpload: (file) => {
        const isValidFormat = ['image/jpeg', 'image/png', 'image/jpg'].includes(file.type);
        if (!isValidFormat) {
          message.error('Solo se permiten formatos .jpg o .png');
          return Upload.LIST_IGNORE;
        }
        const isLt5M = file.size / 1024 / 1024 < 5;
        if (!isLt5M) {
          message.error('El archivo debe pesar menos de 5MB');
          return Upload.LIST_IGNORE;
        }
        setImageFile(file);
        setImageRemoved(false);
        setImagePreview(URL.createObjectURL(file));
        return false;
      },
      onRemove: () => {
        setImageFile(null);
        setImagePreview(undefined);
        setImageRemoved(true);
      },
      showUploadList: false,
    }),
    [message]
  );

  // --- MÉTODOS DE PROGRAMACIÓN SEMANAL Y DÍAS ---

  const getPanelKey = (...parts) => parts.join('::');

  const createUniqueId = (prefix) => (
    `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
  );

  const cloneWeekConfiguration = (sourceWeek, targetWeek) => ({
    ...sourceWeek,
    id: targetWeek.id,
    week_number: targetWeek.week_number,
    title: targetWeek.title,
    days: (sourceWeek.days || []).map((sourceDay) => ({
      ...sourceDay,
      id: createUniqueId('day'),
      blocks: (sourceDay.blocks || []).map((sourceBlock, blockIndex) => {
        const { id: _sourceBlockId, ...blockData } = sourceBlock;
        return {
          ...blockData,
          id: createUniqueId('block'),
          block_number: Number(sourceBlock.block_number ?? blockIndex + 1),
          exercises: (sourceBlock.exercises || []).map((sourceExercise, exerciseIndex) => {
            const { id: _sourceExerciseId, ...exerciseData } = sourceExercise;
            return {
              ...exerciseData,
              id: createUniqueId('ex-entry'),
              exercise_order: Number(sourceExercise.exercise_order ?? exerciseIndex + 1),
              sets_config: (sourceExercise.sets_config || []).map((set, index) => {
                const { id: _sourceSetId, ...setData } = set;
                return {
                  ...setData,
                  set_number: Number(set.set_number ?? index + 1),
                };
              }),
            };
          }),
        };
      }),
    })),
  });

  const isDayConfigurationValid = (day) => {
    if (!day || day.is_rest) return false;
    if (!Array.isArray(day.blocks) || day.blocks.length === 0) return false;

    return day.blocks.every((block) =>
      Array.isArray(block.exercises) &&
      block.exercises.length > 0 &&
      block.exercises.every((exercise) =>
        Number.isInteger(Number(exercise.num_sets)) &&
        Number(exercise.num_sets) > 0 &&
        Array.isArray(exercise.sets_config) &&
        exercise.sets_config.length === Number(exercise.num_sets) &&
        typeof exercise.is_equal_sets === 'boolean'
      )
    );
  };

  const cloneDayConfiguration = (sourceDay, targetDay) => ({
    ...targetDay,
    // El día destino conserva SIEMPRE su identidad y nombre/día de calendario.
    id: targetDay.id,
    title: targetDay.title,
    day_name: targetDay.day_name,
    day_number: targetDay.day_number,
    is_rest: sourceDay.is_rest,
    session_name: sourceDay.session_name || '',
    has_warmup: Boolean(sourceDay.has_warmup),
    warm_up_id: sourceDay.warm_up_id ?? sourceDay.warmup_routine_id ?? null,
    has_cooldown: Boolean(sourceDay.has_cooldown),
    stretch_id: sourceDay.stretch_id ?? sourceDay.cooldown_routine_id ?? null,
    blocks: (sourceDay.blocks || []).map((sourceBlock, blockIndex) => {
      const { id: _sourceBlockId, ...blockData } = sourceBlock;
      return {
        ...blockData,
        id: createUniqueId('block'),
        block_number: Number(sourceBlock.block_number ?? blockIndex + 1),
        exercises: (sourceBlock.exercises || []).map((sourceExercise, exerciseIndex) => {
          const { id: _sourceExerciseId, ...exerciseData } = sourceExercise;
          return {
            ...exerciseData,
            id: createUniqueId('ex-entry'),
            exercise_order: Number(sourceExercise.exercise_order ?? exerciseIndex + 1),
            sets_config: (sourceExercise.sets_config || []).map((set, setIndex) => {
              const { id: _sourceSetId, ...setData } = set;
              return {
                ...setData,
                set_number: Number(set.set_number ?? setIndex + 1),
              };
            }),
          };
        }),
      };
    }),
  });

  const handleOpenCopyWeek = (weekId) => {
    if (weeks.length < 2) {
      message.info('Necesitas al menos dos semanas para copiar una configuración.');
      return;
    }

    setCopySourceWeekId(weekId);
    setCopyTargetWeekIds([]);
    setCopyWeekModalVisible(true);
  };

  const handleCopyWeek = () => {
    if (!copySourceWeekId || copyTargetWeekIds.length === 0) {
      message.warning('Selecciona al menos una semana destino.');
      return;
    }

    if (copyTargetWeekIds.includes(copySourceWeekId)) {
      message.warning('La semana tipo no puede ser una de las semanas destino.');
      return;
    }

    const sourceWeek = weeks.find((week) => week.id === copySourceWeekId);
    const targetWeeks = weeks.filter((week) => copyTargetWeekIds.includes(week.id));

    if (!sourceWeek || targetWeeks.length === 0) return;

    const configuredTargets = targetWeeks.filter((week) =>
      (week.days || []).some((day) =>
        !day.is_rest ||
        (day.blocks || []).length > 0 ||
        day.session_name ||
        day.has_warmup ||
        day.has_cooldown
      )
    );

    const applyCopy = () => {
      setWeeks((prev) => prev.map((week) => {
        if (!copyTargetWeekIds.includes(week.id)) return week;
        return cloneWeekConfiguration(sourceWeek, week);
      }));

      setExpandedWeeks((prev) => prev.filter((key) => !copyTargetWeekIds.includes(Number(key))));
      setExpandedDays((prev) => prev.filter((key) => !copyTargetWeekIds.includes(Number(String(key).split('::')[0]))));
      setExpandedBlocks((prev) => prev.filter((key) => !copyTargetWeekIds.includes(Number(String(key).split('::')[0]))));

      setCopyWeekModalVisible(false);
      setCopySourceWeekId(null);
      setCopyTargetWeekIds([]);
      message.success(`La configuración de ${sourceWeek.title} fue aplicada a ${targetWeeks.length} semana(s).`);
    };

    if (configuredTargets.length > 0) {
      modal.confirm({
        title: 'Las semanas seleccionadas ya contienen programación.',
        icon: <ExclamationCircleOutlined />,
        content: `Al aplicar ${sourceWeek.title} se reemplazará la configuración actual de los días de ${configuredTargets.map((week) => week.title).join(', ')}. Esta acción no se guarda hasta que presiones Guardar, pero la configuración local actual será reemplazada.`,
        okText: 'Continuar',
        okType: 'danger',
        cancelText: 'Cancelar',
        onOk: applyCopy,
      });
      return;
    }

    applyCopy();
  };

  const handleOpenCopyDay = (weekId, dayId) => {
    const sourceWeek = weeks.find((week) => week.id === weekId);
    const sourceDay = sourceWeek?.days.find((day) => day.id === dayId);

    if (!isDayConfigurationValid(sourceDay)) {
      message.warning('Solo puedes clonar un día de entrenamiento que tenga una configuración válida.');
      return;
    }

    setCopySourceDay({ weekId, dayId });
    setCopyTargetWeekId(weekId);
    setCopyTargetDayId(null);
    setCopyDayModalVisible(true);
  };

  const handleCopyDay = () => {
    if (!copySourceDay || !copyTargetWeekId || !copyTargetDayId) {
      message.warning('Selecciona la semana y el día destino.');
      return;
    }

    if (
      copySourceDay.weekId === copyTargetWeekId &&
      copySourceDay.dayId === copyTargetDayId
    ) {
      message.warning('El día destino debe ser diferente al día origen.');
      return;
    }

    const sourceWeek = weeks.find((week) => week.id === copySourceDay.weekId);
    const sourceDay = sourceWeek?.days.find((day) => day.id === copySourceDay.dayId);
    const targetWeek = weeks.find((week) => week.id === copyTargetWeekId);
    const targetDay = targetWeek?.days.find((day) => day.id === copyTargetDayId);

    if (!sourceDay || !targetDay || !isDayConfigurationValid(sourceDay)) {
      message.error('El día origen ya no tiene una configuración válida para clonar.');
      return;
    }

    const applyCopy = () => {
      const clonedDay = cloneDayConfiguration(sourceDay, targetDay);

      setWeeks((prev) => prev.map((week) => {
        if (week.id !== copyTargetWeekId) return week;
        return {
          ...week,
          days: week.days.map((day) => (
            day.id === copyTargetDayId ? clonedDay : day
          )),
        };
      }));

      const targetDayKey = getPanelKey(copyTargetWeekId, copyTargetDayId);
      setExpandedDays((prev) => prev.filter((key) => key !== targetDayKey));
      setExpandedBlocks((prev) => prev.filter((key) => !key.startsWith(`${copyTargetWeekId}::${copyTargetDayId}::`)));

      setCopyDayModalVisible(false);
      setCopySourceDay(null);
      setCopyTargetWeekId(null);
      setCopyTargetDayId(null);
      message.success(`La configuración de ${sourceDay.title} fue clonada a ${targetDay.title}.`);
    };

    modal.confirm({
      title: `¿Reemplazar la configuración de ${targetDay.title}?`,
      icon: <ExclamationCircleOutlined />,
      content: `Se copiará toda la configuración de ${sourceDay.title} sobre ${targetDay.title}. Se reemplazarán su estado, sesión, calentamiento, estiramiento, bloques, ejercicios y series.`,
      okText: 'Sí, reemplazar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: applyCopy,
    });
  };

  const createSetConfig = (exEntry, setNumber) => ({
    set_number: setNumber,
    reps: exEntry.global_config?.reps ?? null,
    seconds: exEntry.global_config?.seconds ?? null,
    weight: exEntry.global_config?.weight ?? null
  });

  const handleAddSeries = (weekId, dayId, blockId, exerciseEntryId) => {
    setWeeks((prev) => prev.map((week) => {
      if (week.id !== weekId) return week;

      return {
        ...week,
        days: week.days.map((day) => {
          if (day.id !== dayId) return day;

          return {
            ...day,
            blocks: day.blocks.map((block) => {
              if (block.id !== blockId) return block;

              return {
                ...block,
                exercises: block.exercises.map((ex) => {
                  if (ex.id !== exerciseEntryId) return ex;

                  const currentSets = ex.sets_config || [];
                  const nextSetNumber = currentSets.length + 1;

                  return {
                    ...ex,
                    num_sets: nextSetNumber,
                    sets_config: [
                      ...currentSets,
                      createSetConfig(ex, nextSetNumber)
                    ]
                  };
                })
              };
            })
          };
        })
      };
    }));
  };

  const handleRemoveSeries = (weekId, dayId, blockId, exerciseEntryId, setIndex = null) => {
    setWeeks((prev) => prev.map((week) => {
      if (week.id !== weekId) return week;

      return {
        ...week,
        days: week.days.map((day) => {
          if (day.id !== dayId) return day;

          return {
            ...day,
            blocks: day.blocks.map((block) => {
              if (block.id !== blockId) return block;

              return {
                ...block,
                exercises: block.exercises.map((ex) => {
                  if (ex.id !== exerciseEntryId) return ex;

                  const currentSets = ex.sets_config || [];
                  if (currentSets.length <= 1) {
                    message.warning('Un ejercicio debe conservar al menos una serie.');
                    return ex;
                  }

                  const newSets = setIndex === null
                    ? currentSets.slice(0, -1)
                    : currentSets.filter((_, index) => index !== setIndex);

                  return {
                    ...ex,
                    num_sets: newSets.length,
                    sets_config: newSets.map((set, index) => ({
                      ...set,
                      set_number: index + 1
                    }))
                  };
                })
              };
            })
          };
        })
      };
    }));
  };

  const handleAddWeek = () => {
    const maxWeeks = durationValueWatched || 1;
    if (weeks.length >= maxWeeks) {
      message.warning(`No puede agregar más semanas de las configuradas en la Duración Estimada (${maxWeeks}).`);
      return;
    }
    const newWeekId = weeks.length + 1;
    setWeeks([
      ...weeks,
      {
        id: newWeekId,
        week_number: newWeekId,
        title: `Semana ${newWeekId}`,
        days: createEmptyDays()
      }
    ]);
  };

  const handleRemoveWeek = (weekId) => {
    modal.confirm({
      title: '¿Eliminar semana?',
      icon: <ExclamationCircleOutlined />,
      content: 'Esta acción eliminará la semana y toda su configuración de entrenamientos.',
      okText: 'Sí, eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk() {
        setWeeks(weeks
          .filter(w => w.id !== weekId)
          .map((week, index) => ({
            ...week,
            id: index + 1,
            week_number: index + 1,
            title: `Semana ${index + 1}`
          }))
        );
      }
    });
  };

  const handleToggleRestDay = (weekId, dayId, isRest) => {
    if (isRest) {
      // Intentar marcar como descanso: advertir eliminación de contenido
      modal.confirm({
        title: '¿Marcar como descanso?',
        icon: <ExclamationCircleOutlined />,
        content: 'El contenido de entrenamiento configurado para este día se eliminará.',
        okText: 'Marcar como descanso',
        okType: 'danger',
        cancelText: 'Cancelar',
        onOk() {
          setWeeks(weeks.map(w => {
            if (w.id === weekId) {
              return {
                ...w,
                days: w.days.map(d => d.id === dayId ? {
                  ...d,
                  is_rest: true,
                  session_name: '',
                  has_warmup: false,
                  warm_up_id: null,
                  has_cooldown: false,
                  stretch_id: null,
                  blocks: []
                } : d)
              };
            }
            return w;
          }));
        }
      });
    } else {
      // Activar día de entrenamiento
      setWeeks(weeks.map(w => {
        if (w.id === weekId) {
          return {
            ...w,
            days: w.days.map(d => d.id === dayId ? { ...d, is_rest: false } : d)
          };
        }
        return w;
      }));
    }
  };

  const handleUpdateDayField = (weekId, dayId, field, value) => {
    setWeeks(weeks.map(w => {
      if (w.id === weekId) {
        return {
          ...w,
          days: w.days.map(d => d.id === dayId ? { ...d, [field]: value } : d)
        };
      }
      return w;
    }));
  };

  // --- BLOQUES Y EJERCICIOS ---

  const renumberBlocks = (blocks) => (blocks || []).map((block, index) => ({
    ...block,
    name: `BLOQUE ${index + 1}`,
    block_number: index + 1
  }));

  const handleAddBlock = (weekId, dayId) => {
    setWeeks(weeks.map(w => {
      if (w.id === weekId) {
        return {
          ...w,
          days: w.days.map(d => {
            if (d.id === dayId) {
              const newBlock = {
                id: createUniqueId('block'),
                name: `BLOQUE ${d.blocks.length + 1}`,
                block_number: d.blocks.length + 1,
                type: 'Lineal',
                exercises: []
              };
              return {
                ...d,
                blocks: renumberBlocks([...d.blocks, newBlock])
              };
            }
            return d;
          })
        };
      }
      return w;
    }));
  };

  const handleRemoveBlock = (weekId, dayId, blockId) => {
    modal.confirm({
      title: '¿Eliminar bloque?',
      icon: <ExclamationCircleOutlined />,
      content: 'Se eliminará el bloque y todos los ejercicios configurados dentro de él.',
      okText: 'Sí, eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk() {
        setWeeks(weeks.map(w => {
          if (w.id === weekId) {
            return {
              ...w,
              days: w.days.map(d => {
                if (d.id === dayId) {
                  return { ...d, blocks: renumberBlocks(d.blocks.filter(b => b.id !== blockId)) };
                }
                return d;
              })
            };
          }
          return w;
        }));
      }
    });
  };

  const handleMoveBlock = (weekId, dayId, blockIndex, direction) => {
    setWeeks(weeks.map(w => {
      if (w.id === weekId) {
        return {
          ...w,
          days: w.days.map(d => {
            if (d.id === dayId) {
              const newBlocks = [...d.blocks];
              const targetIndex = direction === 'up' ? blockIndex - 1 : blockIndex + 1;
              if (targetIndex < 0 || targetIndex >= newBlocks.length) return d;
              const temp = newBlocks[blockIndex];
              newBlocks[blockIndex] = newBlocks[targetIndex];
              newBlocks[targetIndex] = temp;
              return { ...d, blocks: renumberBlocks(newBlocks) };
            }
            return d;
          })
        };
      }
      return w;
    }));
  };

  const openAddExerciseModal = (weekId, dayId, blockId) => {
    setSelectedTarget({ weekId, dayId, blockId });
    setExerciseSearch('');
    setExerciseModalVisible(true);
  };

  const handleSelectExercise = (exercise) => {
    if (!selectedTarget) return;

    const { weekId, dayId, blockId } = selectedTarget;
    const targetWeek = weeks.find((week) => week.id === weekId);
    const targetDay = targetWeek?.days.find((day) => day.id === dayId);
    const targetBlock = targetDay?.blocks.find((block) => block.id === blockId);

    const currentExerciseId = exercise.exercise_id ?? exercise.id;
    const currentEquipmentId = exercise.equipment_id ?? null;

    // const exerciseAlreadyInBlock = targetBlock?.exercises?.some((entry) => {
    //   const sameExercise = String(entry.exercise_id) === String(currentExerciseId);
      
    //   const entryEquipment = entry.equipment_id ?? null;
    //   const sameEquipment = entryEquipment === currentEquipmentId;

    //   return sameExercise && sameEquipment;
    // });

    // if (exerciseAlreadyInBlock) {
    //   message.warning('Este ejercicio ya está agregado en este bloque.');
    //   return;
    // }

    const newExerciseEntry = {
      id: createUniqueId('ex-entry'),
      exercise_order: (targetBlock?.exercises?.length || 0) + 1,
      exercise_id: currentExerciseId,
      exercise_name: exercise.displayLabel,
      equipment_id: currentEquipmentId,
      num_sets: null,
      is_equal_sets: true,
      global_config: { reps: null, seconds: null, weight: null },
      sets_config: []
    };

    setWeeks(weeks.map(w => {
      if (w.id === weekId) {
        return {
          ...w,
          days: w.days.map(d => {
            if (d.id === dayId) {
              return {
                ...d,
                blocks: d.blocks.map(b => {
                  if (b.id === blockId) {
                    return { ...b, exercises: [...(b.exercises || []), newExerciseEntry] };
                  }
                  return b;
                })
              };
            }
            return d;
          })
        };
      }
      return w;
    }));

    setExerciseModalVisible(false);
    setSelectedTarget(null);
  };

  const handleRemoveExercise = (weekId, dayId, blockId, exerciseEntryId) => {
    modal.confirm({
      title: '¿Eliminar ejercicio?',
      icon: <ExclamationCircleOutlined />,
      content: 'Este ejercicio dejará de formar parte de este bloque, pero continuará disponible en el catálogo y en otros planes.',
      okText: 'Sí, eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk() {
        setWeeks(weeks.map(w => {
          if (w.id === weekId) {
            return {
              ...w,
              days: w.days.map(d => {
                if (d.id === dayId) {
                  return {
                    ...d,
                    blocks: d.blocks.map(b => {
                      if (b.id === blockId) {
                        return {
                          ...b,
                          exercises: b.exercises
                            .filter(e => e.id !== exerciseEntryId)
                            .map((exercise, index) => ({
                              ...exercise,
                              exercise_order: index + 1
                            }))
                        };
                      }
                      return b;
                    })
                  };
                }
                return d;
              })
            };
          }
          return w;
        }));
      }
    });
  };

  const handleMoveExercise = (weekId, dayId, blockId, exerciseIndex, direction) => {
    setWeeks(weeks.map(w => {
      if (w.id === weekId) {
        return {
          ...w,
          days: w.days.map(d => {
            if (d.id === dayId) {
              return {
                ...d,
                blocks: d.blocks.map(b => {
                  if (b.id === blockId) {
                    const newExercises = [...b.exercises];
                    const targetIndex = direction === 'up' ? exerciseIndex - 1 : exerciseIndex + 1;
                    if (targetIndex < 0 || targetIndex >= newExercises.length) return b;
                    const temp = newExercises[exerciseIndex];
                    newExercises[exerciseIndex] = newExercises[targetIndex];
                    newExercises[targetIndex] = temp;
                    return {
                       ...b,
                       exercises: newExercises.map((exercise, index) => ({
                         ...exercise,
                         exercise_order: index + 1
                       }))
                     };
                  }
                  return b;
                })
              };
            }
            return d;
          })
        };
      }
      return w;
    }));
  };

  // --- CONFIGURACIÓN DE SERIES EN EJERCICIOS ---

  const handleUpdateExerciseSeries = (weekId, dayId, blockId, exerciseEntryId, field, value) => {
    setWeeks((prev) => prev.map((week) => {
      if (week.id !== weekId) return week;

      return {
        ...week,
        days: week.days.map((day) => {
          if (day.id !== dayId) return day;

          return {
            ...day,
            blocks: day.blocks.map((block) => {
              if (block.id !== blockId) return block;

              return {
                ...block,
                exercises: block.exercises.map((ex) => {
                  if (ex.id !== exerciseEntryId) return ex;

                  if (field === 'is_equal_sets') {
                    if (value === true) {
                      const firstSet = ex.sets_config?.[0];

                      const globalConfig = firstSet
                        ? {
                            reps: firstSet.reps ?? null,
                            seconds: firstSet.seconds ?? null,
                            weight: firstSet.weight ?? null
                          }
                        : { reps: null, seconds: null, weight: null };

                      return {
                        ...ex,
                        is_equal_sets: true,
                        global_config: globalConfig,
                        sets_config: (ex.sets_config || []).map((set, index) => ({
                          ...set,
                          set_number: index + 1,
                          ...globalConfig
                        }))
                      };
                    }

                    const normalizedNumSets = getSeriesCountFromValue(ex.num_sets);

                    // Si no había número de series, se conserva el comportamiento
                    // anterior y se deja pendiente de captura.
                    if (!normalizedNumSets) {
                      return { ...ex, is_equal_sets: false };
                    }

                    const count = normalizedNumSets;
                    const currentSets = ex.sets_config || [];
                    const newSets = Array.from({ length: count }, (_, index) => (
                      currentSets[index] || createSetConfig(ex, index + 1)
                    )).map((set, index) => ({
                      ...set,
                      set_number: index + 1
                    }));

                    return {
                      ...ex,
                      is_equal_sets: false,
                      num_sets: count,
                      sets_config: newSets
                    };
                  }

                  if (field !== 'num_sets') {
                    return { ...ex, [field]: value };
                  }

                  // Las series iguales pueden manejar un valor o un rango.
                  // Las series diferentes conservan el comportamiento anterior y
                  // únicamente permiten un número entero.
                  if (ex.is_equal_sets === true) {
                    const normalizedValue = normalizeRangeValue(value ?? '');

                    if (!validateSeriesValue(normalizedValue)) {
                      return { ...ex, num_sets: normalizedValue };
                    }

                    const count = getSeriesCountFromValue(normalizedValue);
                    const currentSets = ex.sets_config || [];
                    const newSets = count
                      ? Array.from({ length: count }, (_, index) => (
                          currentSets[index] || createSetConfig(ex, index + 1)
                        )).map((set, index) => ({
                          ...set,
                          set_number: index + 1
                        }))
                      : [];

                    return {
                      ...ex,
                      num_sets: normalizedValue || null,
                      sets_config: newSets
                    };
                  }

                  const count = Number(value);
                  if (!Number.isInteger(count) || count < 1) {
                    return { ...ex, num_sets: null, sets_config: [] };
                  }

                  const currentSets = ex.sets_config || [];
                  const newSets = Array.from({ length: count }, (_, index) => (
                    currentSets[index] || createSetConfig(ex, index + 1)
                  )).map((set, index) => ({
                    ...set,
                    set_number: index + 1
                  }));

                  return {
                    ...ex,
                    num_sets: count,
                    sets_config: newSets
                  };
                })
              };
            })
          };
        })
      };
    }));
  };

  const handleUpdateGlobalConfig = (weekId, dayId, blockId, exerciseEntryId, paramField, value) => {
    setWeeks(weeks.map(w => {
      if (w.id === weekId) {
        return {
          ...w,
          days: w.days.map(d => {
            if (d.id === dayId) {
              return {
                ...d,
                blocks: d.blocks.map(b => {
                  if (b.id === blockId) {
                    return {
                      ...b,
                      exercises: b.exercises.map(ex => {
                        if (ex.id === exerciseEntryId) {
                          const newGlobal = { ...ex.global_config, [paramField]: value };
                          // Si las series son iguales, propagar a todas las filas
                          const newSets = ex.sets_config.map(s => ({
                            ...s,
                            [paramField]: value
                          }));
                          return { ...ex, global_config: newGlobal, sets_config: newSets };
                        }
                        return ex;
                      })
                    };
                  }
                  return b;
                })
              };
            }
            return d;
          })
        };
      }
      return w;
    }));
  };

  const handleUpdateSpecificSet = (weekId, dayId, blockId, exerciseEntryId, setIndex, paramField, value) => {
    setWeeks(weeks.map(w => {
      if (w.id === weekId) {
        return {
          ...w,
          days: w.days.map(d => {
            if (d.id === dayId) {
              return {
                ...d,
                blocks: d.blocks.map(b => {
                  if (b.id === blockId) {
                    return {
                      ...b,
                      exercises: b.exercises.map(ex => {
                        if (ex.id === exerciseEntryId) {
                          const newSets = [...ex.sets_config];
                          newSets[setIndex] = { ...newSets[setIndex], [paramField]: value };
                          return { ...ex, sets_config: newSets };
                        }
                        return ex;
                      })
                    };
                  }
                  return b;
                })
              };
            }
            return d;
          })
        };
      }
      return w;
    }));
  };

  // --- VALIDACIÓN Y GUARDADO ---

  const validateWeeklyStructure = () => {
    for (const week of weeks) {
      for (const day of week.days) {
        if (!day.is_rest) {
          if (day.blocks.length === 0) {
            message.error(`El día ${day.title} de la ${week.title} está marcado como entrenamiento pero no contiene bloques.`);
            return false;
          }
          for (const block of day.blocks) {
            if (block.exercises.length === 0) {
              message.error(`El ${block.name} del día ${day.title} (${week.title}) debe contener al menos un ejercicio.`);
              return false;
            }
            for (const ex of block.exercises) {
              if (typeof ex.is_equal_sets !== 'boolean') {
                message.error(`Selecciona si las series de ${ex.exercise_name} son iguales o diferentes.`);
                return false;
              }

              if (ex.is_equal_sets) {
                if (!validateSeriesValue(ex.num_sets)) {
                  message.error(`El número de series de ${ex.exercise_name} debe ser un número entero o un rango de enteros (ej. 10 - 12).`);
                  return false;
                }

                const seriesRange = parseRangeValue(ex.num_sets);
                if (!seriesRange) {
                  message.error(`Selecciona el número de series para ${ex.exercise_name} en ${day.title}.`);
                  return false;
                }
              } else {
                if (!Number.isInteger(Number(ex.num_sets)) || Number(ex.num_sets) < 1) {
                  message.error(`El número de series de ${ex.exercise_name} debe ser un número entero cuando las series son diferentes.`);
                  return false;
                }

                if (!Array.isArray(ex.sets_config) || ex.sets_config.length !== Number(ex.num_sets)) {
                  message.error(`La configuración de series de ${ex.exercise_name} no coincide con el número de series indicado.`);
                  return false;
                }
              }

              const valuesToValidate = ex.sets_config.flatMap((set) => [
                { label: 'repeticiones', value: set.reps, allowZero: false },
                { label: 'segundos', value: set.seconds, allowZero: false },
                { label: 'peso', value: set.weight, allowZero: true },
              ]);

              for (const config of valuesToValidate) {
                if (!validateRangeValue(config.value, config.allowZero)) {
                  message.error(`El valor de ${config.label} de ${ex.exercise_name} debe ser un número o un rango con formato valor - valor.`);
                  return false;
                }
              }
            }
          }
        }
      }
    }
    return true;
  };

  const handleSave = async (shouldPublish = false) => {
    try {
      const values = await form.validateFields();

      if (!validateWeeklyStructure()) {
        return;
      }

      setLoading(true);

      let previewPathToSend = undefined;
      if (imageFile) {
        const uploadResult = await uploadRoutineMedia(imageFile);
        previewPathToSend = uploadResult.url || uploadResult.preview_url;
      } else if (imageRemoved) {
        previewPathToSend = null;
      } else if (isEdit && originalPreviewPath) {
        previewPathToSend = originalPreviewPath;
      }

      const rawContexts = values.contexts || [];
      const parsedContexts = rawContexts.map((ctxStr) => JSON.parse(ctxStr));
      const locationIds = Array.from(new Set(parsedContexts.map((c) => c.routine_location_id)));
      const needsEquipment = parsedContexts.some((c) => c.equipment_id !== null);
      const isSpecial = Boolean(values.is_special_focus);

      const weeklyStructurePayload = weeks.map((week, weekIndex) => ({
        id: week.id,
        week_number: Number(week.week_number ?? weekIndex + 1),
        title: week.title || `Semana ${weekIndex + 1}`,
        days: (week.days || []).map((day, dayIndex) => ({
          ...day,
          day_number: Number(day.day_number ?? dayIndex + 1),
          blocks: (day.blocks || []).map((block, blockIndex) => ({
            ...block,
            block_number: Number(block.block_number ?? blockIndex + 1),
            exercises: (block.exercises || []).map((exercise, exerciseIndex) => ({
              ...exercise,
              exercise_order: Number(exercise.exercise_order ?? exerciseIndex + 1),
              num_sets: exercise.num_sets === null || exercise.num_sets === undefined
                ? null
                : String(exercise.num_sets),
              sets_config: exercise.sets_config?.map((set, setIndex) => ({
                ...set,
                set_number: Number(set.set_number ?? setIndex + 1),
                reps:
                  set.reps != null
                    ? String(set.reps)
                    : null,
                seconds:
                  set.seconds != null
                    ? String(set.seconds)
                    : null,
                weight:
                  set.weight != null
                    ? String(set.weight)
                    : null,
              })) ?? [],
              // sets_config: (exercise.sets_config || []).map((set, setIndex) => ({
              //   ...set,
              //   set_number: Number(set.set_number ?? setIndex + 1)
              // }))
            }))
          }))
        }))
      }));

      const payload = {
        title: values.title.trim(),
        short_description: values.short_description?.trim() || undefined,
        preview_path: previewPathToSend,
        media_path: previewPathToSend,
        routine_level_id: values.routine_level_id,
        routine_focus_id: values.routine_focus_id,
        ...(paradigmEnabled && values.training_paradigm_id
          ? { training_paradigm_id: values.training_paradigm_id }
          : {}),
        is_special_focus: isSpecial,
        special_focus_ids: isSpecial ? values.special_focus_ids || [] : [],
        routine_plan_id: values.routine_plan_id,
        needs_equipment: needsEquipment,
        location_ids: locationIds,
        contexts: parsedContexts,
        frequency_ids: values.frequency_ids || [],
        executor_ids: Array.isArray(values.executor_ids)
          ? values.executor_ids
          : values.executor_ids != null
            ? [values.executor_ids]
            : [],
        duration_config: values.durationValue && values.durationUnit
          ? { value: values.durationValue, unit: values.durationUnit }
          : null,
        weekly_structure: weeklyStructurePayload,
      };

      let targetId = id;
      if (isEdit && id) {
        await updateRoutine(id, payload);
      } else {
        const created = await createRoutine(payload);
        targetId = created?.id;
      }

      saveCompletedRef.current = true;
      setHasUnsavedChanges(false);
      isHydratingRef.current = true;

      if (shouldPublish && targetId) {
        try {
          setPublishErrors([]);

          await publishRoutine(targetId);

          message.success('Plan publicado exitosamente');
        } catch (error) {
          console.log(error);
          const errors = error?.response?.data?.error?.details?.errors || [];

          setPublishErrors(errors);

          message.error(
            error?.response?.data?.error?.message ||
            'El plan tiene errores que deben corregirse antes de publicar.'
          );
          return;
        }
      } else {
        message.success(isEdit ? 'Borrador actualizado' : 'Borrador guardado');
      }

      
      navigate('/routines/all');
    } catch (err) {
      if (err?.errorFields) return;
      const errorMessage = err?.response?.data?.error?.message || err?.message || 'Error al guardar el plan';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const filteredCatalog = useMemo(() => {
    if (!exerciseSearch.trim()) return exerciseCatalog;
    return exerciseCatalog.filter(e =>
      e.displayLabel.toLowerCase().includes(exerciseSearch.toLowerCase())
    );
  }, [exerciseCatalog, exerciseSearch]);

  return (
    <div style={{ padding: '16px 24px' }}>
      {/* Encabezado Superior */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
        <Col>
          <Space align="center">
            
            <Button icon={<ArrowLeftOutlined />} type="text" onClick={() => confirmLeave(() => navigate(-1))} />
            <div>
              <Text type="secondary" style={{ cursor: 'pointer', color: '#1890ff' }} onClick={() => navigate(-1)}>Catálogo de Planes</Text>
              <Title level={3} style={{ margin: 0 }}>
                {isEdit ? 'Editar plan' : 'Nuevo plan'}
              </Title>
            </div>
          </Space>
        </Col>
        <Col>
          <Space size="middle">
            <Tag color={routineStatus === 'PUBLICADO' ? 'green' : 'gold'}>
              {routineStatus}
            </Tag>
            <Button onClick={() => confirmLeave(() => navigate(-1))} disabled={loading}>
              Cancelar
            </Button>
            <Button onClick={() => handleSave(false)} loading={loading}>
              Guardar borrador
            </Button>
            <Button type="primary" onClick={() => handleSave(true)} loading={loading}>
              Publicar plan
            </Button>
          </Space>
        </Col>
      </Row>
      
      {publishErrors.length > 0 && (
        <Alert
          type="error"
          showIcon
          message="El plan tiene errores que deben corregirse antes de publicar"
          description={
            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
              {publishErrors.map((error, index) => (
                <li key={`${error.exercise_id}-${error.week_number}-${error.day_number}-${index}`}>
                  {error.message}
                </li>
              ))}
            </ul>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      <Card loading={loading}>
        <Form
          form={form}
          layout="vertical"
          autoComplete="off"
          initialValues={{ durationValue: 1, durationUnit: 'weeks' }}
          onValuesChange={() => {
            if (!isHydratingRef.current) setHasUnsavedChanges(true);
          }}
        >
          
          <Title level={4}>Datos del plan</Title>

          <Row gutter={16}>
            <Col xs={24} sm={16}>
              <Form.Item
                label="Nombre del plan"
                name="title"
                rules={[
                  { required: true, message: 'El nombre es obligatorio' },
                  { validator: validateTitle },
                ]}
              >
                <Input placeholder="Ej. Fuerza Funcional Casera" allowClear />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label="Nivel"
                name="routine_level_id"
                rules={[{ required: true, message: 'Selecciona un nivel' }]}
              >
                <Select
                  placeholder="Selecciona nivel"
                  options={levels.map((l) => ({ value: l.id, label: l.name || l.code }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={16}>
              <Form.Item
                label="Enfoque"
                name="routine_focus_id"
                rules={[{ required: true, message: 'Selecciona un enfoque' }]}
              >
                <Select
                  placeholder="Selecciona un enfoque"
                  options={normalFocusOptions}
                  allowClear
                  onChange={(value) => {
                    const selected = focuses.find((f) => f.id === value);
                    if (selected?.code !== 'hypertrophy') {
                      form.setFieldValue('training_paradigm_id', undefined);
                    }
                  }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item
                label="Precio / Plan de Cobro"
                name="routine_plan_id"
                rules={[{ required: true, message: 'Selecciona un plan de cobro' }]}
              >
                <Select
                  placeholder="Selecciona un plan"
                  options={plans.map((p) => ({ value: p.id, label: p.name || p.code }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16} align="bottom">
            <Col xs={24} sm={12}>
              <Form.Item name="is_special_focus" valuePropName="checked" style={{ marginBottom: 24 }}>
                <Checkbox>¿Es un enfoque especial?</Checkbox>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Paradigma"
                name="training_paradigm_id"
                rules={paradigmEnabled ? [{ required: true, message: 'Selecciona un paradigma' }] : []}
              >
                <Select
                  placeholder="Selecciona un paradigma"
                  options={paradigmOptions}
                  disabled={!paradigmEnabled}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>

          {isSpecialFocusWatched && (
            <Row gutter={16}>
              <Col xs={24}>
                <Form.Item
                  label="Enfoque especial"
                  name="special_focus_ids"
                  rules={[{ required: true, message: 'Selecciona al menos un enfoque especial' }]}
                >
                  <Select
                    mode="multiple"
                    placeholder="Selecciona uno o más enfoques especiales"
                    options={specialFocusOptions}
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item
                label="Contexto / equipo"
                name="contexts"
                rules={[{ required: true, message: 'Selecciona al menos un contexto' }]}
              >
                <Select
                  mode="multiple"
                  placeholder="Selecciona uno o varios contextos"
                  options={contextOptions}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Duración estimada" style={{ marginBottom: 0 }}>
                <Space.Compact style={{ width: '100%' }}>
                  <Form.Item name="durationValue" noStyle>
                    <InputNumber 
                      placeholder="Valor" 
                      min={1} 
                      style={{ width: '60%' }} 
                      onChange={handleDurationChange}
                    />
                  </Form.Item>
                  <Form.Item name="durationUnit" noStyle>
                    <Select
                      placeholder="Unidad"
                      style={{ width: '40%' }}
                      options={[{ value: 'weeks', label: 'Semanas' }]}
                    />
                  </Form.Item>
                </Space.Compact>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                label="Frecuencia recomendada"
                name="frequency_ids"
                rules={[{ required: true, message: 'Selecciona al menos una frecuencia' }]}
              >
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  placeholder="Selecciona frecuencias permitidas"
                  options={durations.map((d) => ({ value: d.id, label: d.name || `${d.days} días` }))}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col xs={24}>
              <Form.Item
                label="Descripción corta"
                name="short_description"
                rules={[
                  { required: true, message: 'La descripción corta es requerida' },
                  { max: 150, message: 'Máximo 150 caracteres' },
                ]}
              >
                <Input
                  placeholder="Para el detalle del plan en la app (máx 150 caracteres)"
                  maxLength={150}
                  showCount
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24}>
            <Text type="secondary" style={{ fontSize: 13 }}>Si no cargas una, se usa la imagen default del sistema.</Text>
              <Form.Item label="Imagen del plan (opcional)">
                {imagePreview ? (
                  <div style={{ marginBottom: 16 }}>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{ width: 200, height: 120, objectFit: 'cover', borderRadius: 8 }}
                    />
                    <Button
                      type="link"
                      danger
                      onClick={() => {
                        setImageFile(null);
                        setImagePreview(undefined);
                        setImageRemoved(true);
                      }}
                    >
                      Eliminar imagen
                    </Button>
                  </div>
                ) : null}
                <Dragger {...uploadProps}>
                  <p className="ant-upload-drag-icon">
                    <PictureOutlined style={{ fontSize: 32, color: '#1890ff' }} />
                  </p>
                  <p className="ant-upload-text">Subir imagen (opcional)</p>
                </Dragger>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item label="Avatares disponibles para este plan" name="executor_ids">
                <div>
                  <Text type="secondary" style={{ fontSize: 13 }}>Asegúrate de que los ejercicios de este plan tengan contenido (video/audio) para cada avatar que marques aquí.</Text>
                  <Checkbox.Group style={{ width: '100%' }}>
                    <Row gutter={[16, 16]}>
                      {executors.map((exec) => (
                        <Col key={exec.id}>
                          <Checkbox value={exec.id}>{exec.name}</Checkbox>
                        </Col>
                      ))}
                    </Row>
                  </Checkbox.Group>
                </div>
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          {/* ========================================================= */}
          {/* PROGRAMACIÓN DETALLADA DEL PLAN (SEMANAS -> DÍAS -> BLOQUES) */}
          {/* ========================================================= */}
          <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
            <Col>
              <Title level={4} style={{ margin: 0 }}>Estructura Semanal</Title>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Construye la estructura semanal. Límite máximo según la duración: {durationValueWatched || 1} semana(s).
              </Text>
            </Col>
            <Col>
              <Button 
                type="dashed" 
                icon={<PlusOutlined />} 
                onClick={handleAddWeek}
                disabled={weeks.length >= (durationValueWatched || 1)}
              >
                Agregar Semana
              </Button>
            </Col>
          </Row>

          {weeks.map((week) => (
            <Collapse
              key={week.id}
              activeKey={expandedWeeks}
              onChange={(keys) => setExpandedWeeks(Array.isArray(keys) ? keys : [keys])}
              style={{ marginBottom: 16, backgroundColor: '#FAF9F6', borderColor: '#E5E1DA' }}
              items={[
                {
                  key: String(week.id),
                  label: <Text strong style={{ fontSize: 16 }}>{week.title}</Text>,
                  extra: (
                    <div onClick={(event) => event.stopPropagation()}>
                      <Space>
                      <Button
                        size="small"
                        type="default"
                        onClick={() => handleOpenCopyWeek(week.id)}
                      >
                        Usar como semana tipo
                      </Button>
                      {weeks.length > 1 && (
                        <Button
                          size="small"
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleRemoveWeek(week.id)}
                        >
                          Eliminar semana
                        </Button>
                      )}
                      </Space>
                    </div>
                  ),
                  children: (
                    <Collapse
                      ghost
                      activeKey={expandedDays.filter((key) => key.startsWith(`${week.id}::`))}
                      onChange={(keys) => {
                        const weekPrefix = `${week.id}::`;
                        const nextKeys = Array.isArray(keys) ? keys : [keys];
                        setExpandedDays((prev) => [
                          ...prev.filter((key) => !key.startsWith(weekPrefix)),
                          ...nextKeys
                            .filter((key) => String(key).startsWith(weekPrefix))
                            .map(String)
                        ]);
                      }}
                      items={week.days.map((day) => {
                        const dayKey = getPanelKey(week.id, day.id);
                        const warmupRoutine = miniRoutinesCatalog.find(m => m.id === day.warm_up_id);
                        const cooldownRoutine = miniRoutinesCatalog.find(m => m.id === day.stretch_id);

                        return {
                          key: dayKey,
                          label: (
                            <Space align="center" size="middle">
                              <Text strong style={{ minWidth: 90 }}>{day.title}</Text>
                              <Tag color={day.is_rest ? 'default' : 'blue'}>
                                {day.is_rest ? '💤 Descanso' : '🏋️ Entrenamiento'}
                              </Tag>
                              {!day.is_rest && (
                                <Text type="secondary">
                                  {day.session_name || 'Sin nombre'} — {day.blocks.length} bloque(s)
                                </Text>
                              )}
                            </Space>
                          ),
                          extra: (
                            <div onClick={(event) => event.stopPropagation()}>
                              <Space>
                                {!day.is_rest && (
                                  <Tooltip title="Clonar día">
                                    <Button
                                      size="small"
                                      onClick={() => handleOpenCopyDay(week.id, day.id)}
                                    >
                                      Clonar día
                                    </Button>
                                  </Tooltip>
                                )}
                                <Switch
                                  checked={!day.is_rest}
                                  checkedChildren="Entrenamiento"
                                  unCheckedChildren="Descanso"
                                  onClick={(_, event) => event?.stopPropagation?.()}
                                  onChange={(checked) => handleToggleRestDay(week.id, day.id, !checked)}
                                />
                              </Space>
                            </div>
                          ),
                          children: !day.is_rest ? (
                            <div style={{ padding: '4px 8px 8px', borderLeft: '3px solid #1890ff' }}>
                              <Row gutter={16} style={{ marginBottom: 16 }}>
                                <Col xs={24}>
                                  <Text type="secondary" style={{ fontSize: 12 }}>
                                    Nombre de sesión (opcional):
                                  </Text>
                                  <Input
                                    placeholder="Ej. Tren Superior / Pecho"
                                    value={day.session_name}
                                    onChange={(e) => handleUpdateDayField(
                                      week.id,
                                      day.id,
                                      'session_name',
                                      e.target.value
                                    )}
                                  />
                                </Col>
                              </Row>

                              <Card size="small" style={{ marginBottom: 16, backgroundColor: '#FAFAFA' }}>
                                <Checkbox
                                  checked={day.has_warmup}
                                  onChange={(e) => handleUpdateDayField(
                                    week.id,
                                    day.id,
                                    'has_warmup',
                                    e.target.checked
                                  )}
                                >
                                  <Text strong>🔥 Agregar Calentamiento</Text>
                                </Checkbox>

                                {day.has_warmup && (
                                  <div style={{ marginTop: 8, paddingLeft: 24 }}>
                                    <Select
                                      placeholder="Selecciona minirutina de calentamiento"
                                      style={{ width: '100%', marginBottom: 8 }}
                                      value={day.warm_up_id}
                                      onChange={(val) => handleUpdateDayField(
                                        week.id,
                                        day.id,
                                        'warm_up_id',
                                        val
                                      )}
                                      options={miniRoutinesCatalog
                                        .filter(m => m.type?.code === 'WARMUP' || m.type?.code === 'CALENTAMIENTO')
                                        .map(m => ({ value: m.id, label: m.title }))}
                                      allowClear
                                    />
                                    {warmupRoutine?.exercises && (
                                      <div style={{
                                        background: '#FFF',
                                        padding: 8,
                                        borderRadius: 4,
                                        border: '1px solid #E8E8E8'
                                      }}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                          Ejercicios incluidos:
                                        </Text>
                                        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
                                          {warmupRoutine.exercises.map((ex, idx) => (
                                            <li key={idx}>{ex.exercise?.name || ex.exercise?.title}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </Card>

                              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                                Bloques de Ejercicios:
                              </Text>

                              <Collapse
                                ghost
                                activeKey={expandedBlocks.filter((key) => key.startsWith(`${week.id}::${day.id}::`))}
                                onChange={(keys) => {
                                  const dayPrefix = `${week.id}::${day.id}::`;
                                  const nextKeys = Array.isArray(keys) ? keys : [keys];
                                  setExpandedBlocks((prev) => [
                                    ...prev.filter((key) => !key.startsWith(dayPrefix)),
                                    ...nextKeys
                                      .filter((key) => String(key).startsWith(dayPrefix))
                                      .map(String)
                                  ]);
                                }}
                                items={day.blocks.map((block, blockIdx) => {
                                  const blockKey = getPanelKey(week.id, day.id, block.id);

                                  return {
                                    key: blockKey,
                                    label: (
                                      <Space>
                                        <Tag color="blue">{block.type}</Tag>
                                        <Text strong>{`BLOQUE ${blockIdx + 1}`}</Text>
                                        <Text type="secondary">
                                          ({block.exercises.length} ejercicio{block.exercises.length === 1 ? '' : 's'})
                                        </Text>
                                      </Space>
                                    ),
                                    extra: (
                                      <div onClick={(event) => event.stopPropagation()}>
                                        <Space>
                                        <Button
                                          size="small"
                                          icon={<UpOutlined />}
                                          disabled={blockIdx === 0}
                                          onClick={() => handleMoveBlock(week.id, day.id, blockIdx, 'up')}
                                        />
                                        <Button
                                          size="small"
                                          icon={<DownOutlined />}
                                          disabled={blockIdx === day.blocks.length - 1}
                                          onClick={() => handleMoveBlock(week.id, day.id, blockIdx, 'down')}
                                        />
                                        <Button
                                          size="small"
                                          type="text"
                                          danger
                                          icon={<DeleteOutlined />}
                                          onClick={() => handleRemoveBlock(week.id, day.id, block.id)}
                                        />
                                        </Space>
                                      </div>
                                    ),
                                    children: (
                                      <>
                                        {block.exercises.map((exEntry, exIdx) => (
                                          <Card
                                            key={exEntry.id}
                                            size="small"
                                            style={{ marginBottom: 12, backgroundColor: '#FFFFFF' }}
                                            title={<Text strong>{exIdx + 1}. {exEntry.exercise_name}</Text>}
                                            extra={
                                              <Space>
                                                <Button
                                                  size="small"
                                                  icon={<UpOutlined />}
                                                  disabled={exIdx === 0}
                                                  onClick={() => handleMoveExercise(
                                                    week.id, day.id, block.id, exIdx, 'up'
                                                  )}
                                                />
                                                <Button
                                                  size="small"
                                                  icon={<DownOutlined />}
                                                  disabled={exIdx === block.exercises.length - 1}
                                                  onClick={() => handleMoveExercise(
                                                    week.id, day.id, block.id, exIdx, 'down'
                                                  )}
                                                />
                                                <Button
                                                  size="small"
                                                  type="text"
                                                  danger
                                                  icon={<DeleteOutlined />}
                                                  onClick={() => handleRemoveExercise(
                                                    week.id, day.id, block.id, exEntry.id
                                                  )}
                                                />
                                              </Space>
                                            }
                                          >
                                            <Row gutter={16} align="middle" style={{ marginBottom: 12 }}>
                                              <Col>
                                                <Space align="center">
                                                  <Text style={{ fontSize: 12 }}>Número de series:</Text>
                                                  {exEntry.is_equal_sets ? (
                                                    <RangeValueInput
                                                      placeholder="Ej. 3 o 10 - 12"
                                                      value={exEntry.num_sets}
                                                      onChange={(val) => handleUpdateExerciseSeries(
                                                        week.id, day.id, block.id, exEntry.id, 'num_sets', val
                                                      )}
                                                    />
                                                  ) : (
                                                    <InputNumber
                                                      min={1}
                                                      precision={0}
                                                      placeholder="Ej. 3"
                                                      value={exEntry.num_sets ?? undefined}
                                                      onChange={(val) => handleUpdateExerciseSeries(
                                                        week.id, day.id, block.id, exEntry.id, 'num_sets', val
                                                      )}
                                                      style={{ width: 100 }}
                                                    />
                                                  )}
                                                </Space>
                                              </Col>
                                              <Col>
                                                <Space align="center">
                                                  <Text style={{ fontSize: 12 }}>¿Series iguales?</Text>
                                                  <Select
                                                    placeholder="Selecciona"
                                                    value={
                                                      exEntry.is_equal_sets === null || exEntry.is_equal_sets === undefined
                                                        ? undefined
                                                        : (exEntry.is_equal_sets ? 'YES' : 'NO')
                                                    }
                                                    onChange={(val) => handleUpdateExerciseSeries(
                                                      week.id, day.id, block.id, exEntry.id,
                                                      'is_equal_sets', val === 'YES'
                                                    )}
                                                    options={[
                                                      { value: 'YES', label: 'Sí' },
                                                      { value: 'NO', label: 'No' }
                                                    ]}
                                                    style={{ width: 80 }}
                                                  />
                                                </Space>
                                              </Col>
                                            </Row>

                                            {!exEntry.num_sets ? (
                                              <div style={{
                                                padding: '12px 16px',
                                                background: '#FAFAFA',
                                                borderRadius: 4,
                                                color: '#8C8C8C'
                                              }}>
                                                Selecciona primero el número de series para configurar el ejercicio.
                                              </div>
                                            ) : typeof exEntry.is_equal_sets !== 'boolean' ? (
                                              <div style={{
                                                padding: '12px 16px',
                                                background: '#FAFAFA',
                                                borderRadius: 4,
                                                color: '#8C8C8C'
                                              }}>
                                                Selecciona si las series serán iguales o diferentes para continuar.
                                              </div>
                                            ) : exEntry.is_equal_sets ? (
                                              <Row
                                                gutter={12}
                                                align="middle"
                                                style={{
                                                  background: '#FAFAFA',
                                                  padding: 8,
                                                  borderRadius: 4
                                                }}
                                              >
                                                <Col xs={24} sm={8}>
                                                  <Text style={{ fontSize: 12 }}>Repeticiones:</Text>
                                                  <RangeValueInput
                                                    placeholder="Ej. 10 o 10 - 12"
                                                    value={exEntry.global_config.reps}
                                                    onChange={(val) => handleUpdateGlobalConfig(
                                                      week.id, day.id, block.id, exEntry.id, 'reps', val
                                                    )}
                                                  />
                                                </Col>
                                                <Col xs={24} sm={8}>
                                                  <Text style={{ fontSize: 12 }}>Segundos (Seg):</Text>
                                                  <RangeValueInput
                                                    placeholder="Ej. 30 o 60 - 90"
                                                    value={exEntry.global_config.seconds}
                                                    onChange={(val) => handleUpdateGlobalConfig(
                                                      week.id, day.id, block.id, exEntry.id, 'seconds', val
                                                    )}
                                                  />
                                                </Col>
                                                <Col xs={24} sm={8}>
                                                  <Text style={{ fontSize: 12 }}>Peso (kg):</Text>
                                                  <RangeValueInput
                                                    placeholder="Ej. 20 o 10 - 20"
                                                    value={exEntry.global_config.weight}
                                                    onChange={(val) => handleUpdateGlobalConfig(
                                                      week.id, day.id, block.id, exEntry.id, 'weight', val
                                                    )}
                                                    allowZero
                                                  />
                                                </Col>
                                              </Row>
                                            ) : (
                                              <>
                                              <Table
                                                size="small"
                                                pagination={false}
                                                dataSource={exEntry.sets_config}
                                                rowKey="set_number"
                                                columns={[
                                                  {
                                                    title: 'Serie',
                                                    dataIndex: 'set_number',
                                                    width: 70,
                                                    render: (n) => <Text strong>#{n}</Text>
                                                  },
                                                  {
                                                    title: 'Reps',
                                                    render: (_, record, sIdx) => (
                                                      <RangeValueInput
                                                        placeholder="Ej. 10 o 10 - 12"
                                                        value={record.reps}
                                                        onChange={(val) => handleUpdateSpecificSet(
                                                          week.id,
                                                          day.id,
                                                          block.id,
                                                          exEntry.id,
                                                          sIdx,
                                                          'reps',
                                                          val
                                                        )}
                                                      />
                                                    )
                                                  },
                                                  {
                                                    title: 'Seg',
                                                    render: (_, record, sIdx) => (
                                                      <RangeValueInput
                                                        placeholder="Ej. 30 o 60 - 90"
                                                        value={record.seconds}
                                                        onChange={(val) => handleUpdateSpecificSet(
                                                          week.id,
                                                          day.id,
                                                          block.id,
                                                          exEntry.id,
                                                          sIdx,
                                                          'seconds',
                                                          val
                                                        )}
                                                      />
                                                    )
                                                  },
                                                  {
                                                    title: 'Peso (kg)',
                                                    render: (_, record, sIdx) => (
                                                      <RangeValueInput
                                                        placeholder="Ej. 20 o 10 - 20"
                                                        value={record.weight}
                                                        onChange={(val) => handleUpdateSpecificSet(
                                                          week.id,
                                                          day.id,
                                                          block.id,
                                                          exEntry.id,
                                                          sIdx,
                                                          'weight',
                                                          val
                                                        )}
                                                        allowZero
                                                      />
                                                    )
                                                  },
                                                  {
                                                    title: '',
                                                    width: 70,
                                                    render: (_, record, sIdx) => (
                                                      <Button
                                                        size="small"
                                                        type="text"
                                                        danger
                                                        disabled={exEntry.sets_config.length <= 1}
                                                        onClick={() => handleRemoveSeries(
                                                          week.id, day.id, block.id, exEntry.id, sIdx
                                                        )}
                                                      >
                                                        Quitar
                                                      </Button>
                                                    )
                                                  }
                                                ]}
                                              />
                                              <Button
                                                type="dashed"
                                                size="small"
                                                icon={<PlusOutlined />}
                                                onClick={() => handleAddSeries(
                                                  week.id, day.id, block.id, exEntry.id
                                                )}
                                                style={{ marginTop: 8 }}
                                              >
                                                Agregar serie
                                              </Button>
                                              </>
                                            )}
                                          </Card>
                                        ))}

                                        <Button
                                          type="dashed"
                                          size="small"
                                          block
                                          icon={<PlusOutlined />}
                                          onClick={() => openAddExerciseModal(
                                            week.id, day.id, block.id
                                          )}
                                        >
                                          Agregar ejercicio
                                        </Button>
                                      </>
                                    )
                                  };
                                })}
                              />

                              <Button
                                type="dashed"
                                icon={<PlusOutlined />}
                                onClick={() => handleAddBlock(week.id, day.id)}
                                style={{ marginTop: 16, marginBottom: 16 }}
                              >
                                Agregar bloque
                              </Button>

                              <Card size="small" style={{ backgroundColor: '#FAFAFA' }}>
                                <Checkbox
                                  checked={day.has_cooldown}
                                  onChange={(e) => handleUpdateDayField(
                                    week.id, day.id, 'has_cooldown', e.target.checked
                                  )}
                                >
                                  <Text strong>🧘 Agregar Estiramiento</Text>
                                </Checkbox>

                                {day.has_cooldown && (
                                  <div style={{ marginTop: 8, paddingLeft: 24 }}>
                                    <Select
                                      placeholder="Selecciona minirutina de estiramiento"
                                      style={{ width: '100%', marginBottom: 8 }}
                                      value={day.stretch_id}
                                      onChange={(val) => handleUpdateDayField(
                                        week.id, day.id, 'stretch_id', val
                                      )}
                                      options={miniRoutinesCatalog
                                        .filter(m => m.type?.code === 'COOLDOWN' || m.type?.code === 'ESTIRAMIENTO')
                                        .map(m => ({ value: m.id, label: m.title }))}
                                      allowClear
                                    />
                                    {cooldownRoutine?.exercises && (
                                      <div style={{
                                        background: '#FFF',
                                        padding: 8,
                                        borderRadius: 4,
                                        border: '1px solid #E8E8E8'
                                      }}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                          Ejercicios incluidos:
                                        </Text>
                                        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
                                          {cooldownRoutine.exercises.map((ex, idx) => (
                                            <li key={idx}>{ex.exercise?.name || ex.exercise?.title}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </Card>
                            </div>
                          ) : null
                        };
                      })}
                    />
                  )
                }
              ]}
            />
          ))}

        </Form>
      </Card>

      <Modal
        title="Usar como semana tipo"
        open={copyWeekModalVisible}
        onCancel={() => {
          setCopyWeekModalVisible(false);
          setCopySourceWeekId(null);
          setCopyTargetWeekIds([]);
        }}
        okText="Continuar"
        cancelText="Cancelar"
        onOk={handleCopyWeek}
        okButtonProps={{ disabled: copyTargetWeekIds.length === 0 }}
      >
        <Text>
          Selecciona una o varias semanas destino. Toda su configuración será reemplazada por la
          configuración de la semana seleccionada como tipo.
        </Text>
        <div style={{ marginTop: 16 }}>
          <Text strong>Semanas destino</Text>
          <Select
            mode="multiple"
            style={{ width: '100%', marginTop: 8 }}
            placeholder="Selecciona las semanas que deseas reemplazar"
            value={copyTargetWeekIds}
            onChange={setCopyTargetWeekIds}
            options={weeks
              .filter((week) => week.id !== copySourceWeekId)
              .map((week) => ({
                value: week.id,
                label: week.title
              }))}
          />
        </div>
      </Modal>

      {/* MODAL PARA CLONAR UN DÍA A OTRA SEMANA/DÍA */}
      <Modal
        title="Clonar día"
        open={copyDayModalVisible}
        onCancel={() => {
          setCopyDayModalVisible(false);
          setCopySourceDay(null);
          setCopyTargetWeekId(null);
          setCopyTargetDayId(null);
        }}
        okText="Continuar"
        cancelText="Cancelar"
        onOk={handleCopyDay}
        okButtonProps={{ disabled: !copyTargetWeekId || !copyTargetDayId }}
      >
        <Text>
          Selecciona la semana y el día destino. Se copiará toda la configuración del día origen
          y el día destino conservará su identidad y nombre de calendario.
        </Text>

        <div style={{ marginTop: 16 }}>
          <Text strong>Semana destino</Text>
          <Select
            style={{ width: '100%', marginTop: 8 }}
            placeholder="Selecciona la semana destino"
            value={copyTargetWeekId ?? undefined}
            onChange={(value) => {
              setCopyTargetWeekId(value);
              setCopyTargetDayId(null);
            }}
            options={weeks.map((week) => ({
              value: week.id,
              label: week.title,
            }))}
          />
        </div>

        <div style={{ marginTop: 16 }}>
          <Text strong>Día destino</Text>
          <Select
            style={{ width: '100%', marginTop: 8 }}
            placeholder="Selecciona el día destino"
            value={copyTargetDayId ?? undefined}
            onChange={setCopyTargetDayId}
            options={(weeks.find((week) => week.id === copyTargetWeekId)?.days || [])
              .filter((day) => !(copySourceDay?.weekId === copyTargetWeekId && day.id === copySourceDay?.dayId))
              .map((day) => ({
                value: day.id,
                label: day.title,
              }))}
          />
        </div>
      </Modal>

      {/* MODAL PARA BUSCAR Y AGREGAR EJERCICIOS DEL CATÁLOGO */}
      <Modal
        title="Catálogo de Ejercicios"
        open={exerciseModalVisible}
        onCancel={() => setExerciseModalVisible(false)}
        footer={null}
        width={600}
      >
        <Input
          prefix={<SearchOutlined />}
          placeholder="Buscar ejercicio por nombre..."
          value={exerciseSearch}
          onChange={(e) => setExerciseSearch(e.target.value)}
          style={{ marginBottom: 16 }}
          allowClear
        />
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          {filteredCatalog.map((exercise) => {
            // const targetWeek = weeks.find((week) => week.id === selectedTarget?.weekId);
            // const targetDay = targetWeek?.days.find((day) => day.id === selectedTarget?.dayId);
            // const targetBlock = targetDay?.blocks.find((block) => block.id === selectedTarget?.blockId);

            const currentExerciseId = exercise.exercise_id ?? exercise.id;
            const currentEquipmentId = exercise.equipment_id ?? null;

            // const alreadyAdded = targetBlock?.exercises?.some((entry) => {
            //   const sameExercise = String(entry.exercise_id) === String(currentExerciseId);
            //   const sameEquipment = (entry.equipment_id ?? null) === currentEquipmentId;

            //   return sameExercise && sameEquipment;
            // });

            const itemKey = `${currentExerciseId}-${currentEquipmentId || 'no-eq'}`;

            return (
              <div
                key={itemKey}
                style={{
                  padding: '10px 14px',
                  borderBottom: '1px solid #F0F0F0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div>
                  <Text bold>{exercise.displayLabel}</Text>
                </div>
                <Button
                  type="primary"
                  size="small"
                  // disabled={alreadyAdded}
                  onClick={() => handleSelectExercise(exercise)}
                >
                  Seleccionar
                  {/* {alreadyAdded ? 'Ya agregado' : 'Seleccionar'} */}
                </Button>
              </div>
            );
          })}

          {filteredCatalog.length === 0 && (
            <Text type="secondary" style={{ display: 'block', textAlign: 'center', padding: 20 }}>
              No se encontraron ejercicios en el catálogo.
            </Text>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default RoutineUpsertPage;
